import { useEffect, useRef, useState } from "react";

import { AddCommentOutlined, ArrowUpwardRounded, StopRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Chip,
	FormControl,
	IconButton,
	InputLabel,
	List,
	ListItemButton,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";

import { getInsightsConfig, getTopInsightsQueries } from "../../apis/insights";
import { ChatMessage } from "../../components/Insights/ChatMessage";
import { useInsightsChat } from "../../components/Insights/useInsightsChat";

const TOP_QUERIES_LIMIT = 20;

export function OutfitInsights() {
	const chat = useInsightsChat();
	const [config, setConfig] = useState(null);
	const [configError, setConfigError] = useState(null);
	const [database, setDatabase] = useState("");
	const [draft, setDraft] = useState("");
	const [topQueries, setTopQueries] = useState(null);
	const scrollRef = useRef(null);
	const inputRef = useRef(null);

	useEffect(() => {
		let cancelled = false;
		getInsightsConfig()
			.then((result) => {
				if (cancelled) {
					return;
				}
				setConfig(result);
				setDatabase(result.default_database);
			})
			.catch((error) => !cancelled && setConfigError(error.message));
		return () => {
			cancelled = true;
		};
	}, []);

	const activeDatabase = chat.database || database;
	const hasConversation = chat.messages.length > 0;
	const ready = Boolean(config) && !configError;

	// Reload on every return to the empty screen, so questions just asked are counted.
	useEffect(() => {
		if (!ready || hasConversation) {
			return;
		}
		let cancelled = false;
		getTopInsightsQueries(TOP_QUERIES_LIMIT)
			.then((queries) => !cancelled && setTopQueries(queries))
			.catch(() => !cancelled && setTopQueries([]));
		return () => {
			cancelled = true;
		};
	}, [ready, hasConversation]);

	// Follow the answer as it streams, unless the user has scrolled up to read.
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
		if (nearBottom || chat.streaming) {
			el.scrollTo({ top: el.scrollHeight });
		}
	}, [chat.messages, chat.streaming]);

	const submit = (text) => {
		if (!text.trim() || chat.streaming || !config) {
			return;
		}
		chat.send(text, database);
		setDraft("");
	};

	const newChat = () => {
		chat.reset();
		setDraft("");
		inputRef.current?.focus();
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 144px)", minHeight: 480 }}>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
				<Box sx={{ flexGrow: 1 }}>
					<Typography variant="h2" component="h1" sx={{ m: 0 }}>
						Insights
					</Typography>
					<Typography variant="body2" sx={{ color: "text.secondary" }}>
						Ask about outfits, the products people combine, and how those looks perform.
					</Typography>
				</Box>
				{config && (
					<Tooltip title={hasConversation ? "Start a new chat to switch database" : ""}>
						<FormControl size="small" sx={{ minWidth: 200 }} disabled={hasConversation}>
							<InputLabel id="insights-database-label">Database</InputLabel>
							<Select labelId="insights-database-label" label="Database" value={activeDatabase} onChange={(e) => setDatabase(e.target.value)}>
								{config.databases.map((db) => (
									<MenuItem key={db.id} value={db.id}>
										{db.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Tooltip>
				)}
				<Button variant="outlined" startIcon={<AddCommentOutlined />} onClick={newChat} disabled={!hasConversation}>
					New chat
				</Button>
			</Stack>

			<Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: "auto", px: { xs: 2, md: 4 }, py: 1 }}>
				{configError && (
					<Alert severity="error" sx={{ mt: 2 }}>
						{configError}
					</Alert>
				)}
				{!hasConversation && ready && (
					<Box sx={{ maxWidth: 920, mx: "auto", pt: { xs: 2, md: 6 } }}>
						<Typography variant="h3" sx={{ mb: 2 }}>
							What would you like to know?
						</Typography>
						{topQueries?.length > 0 && (
							<>
								<Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
									Frequently asked
								</Typography>
								<List disablePadding sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
									{topQueries.map((q, index) => (
										<ListItemButton key={q.text} divider={index < topQueries.length - 1} onClick={() => submit(q.text)}>
											<ListItemText primary={q.text} primaryTypographyProps={{ variant: "body2" }} />
											<Tooltip title={`Asked ${q.count} ${q.count === 1 ? "time" : "times"}`}>
												<Chip label={q.count} size="small" variant="outlined" sx={{ ml: 2, flexShrink: 0 }} />
											</Tooltip>
										</ListItemButton>
									))}
								</List>
							</>
						)}
						{topQueries?.length === 0 && (
							<Typography variant="body2" sx={{ color: "text.secondary" }}>
								The questions asked most often will show up here.
							</Typography>
						)}
					</Box>
				)}
				<Box sx={{ maxWidth: 1040, mx: "auto" }}>
					{chat.messages.map((message) => (
						<ChatMessage key={message.id} message={message} onNewChat={newChat} />
					))}
				</Box>
			</Box>

			<Box sx={{ px: { xs: 2, md: 4 }, py: 2, borderTop: 1, borderColor: "divider" }}>
				<Stack direction="row" spacing={1} alignItems="flex-end" sx={{ maxWidth: 1040, mx: "auto" }}>
					<TextField
						inputRef={inputRef}
						fullWidth
						multiline
						maxRows={6}
						placeholder={ready ? "Ask about outfits, combinations, brands, playoffs…" : "Connecting…"}
						value={draft}
						disabled={!ready}
						onChange={(e) => setDraft(e.target.value.slice(0, 4000))}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
								e.preventDefault();
								submit(draft);
							}
						}}
						inputProps={{ "aria-label": "Your question" }}
					/>
					{chat.streaming ? (
						<Tooltip title="Stop">
							<IconButton color="primary" onClick={chat.stop} aria-label="Stop answering" sx={{ border: 1, borderColor: "divider" }}>
								<StopRounded />
							</IconButton>
						</Tooltip>
					) : (
						<Tooltip title="Send (Enter)">
							<span>
								<IconButton
									color="primary"
									onClick={() => submit(draft)}
									disabled={!draft.trim() || !ready}
									aria-label="Send question"
									sx={{ bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" }, "&.Mui-disabled": { bgcolor: "action.disabledBackground" } }}
								>
									<ArrowUpwardRounded />
								</IconButton>
							</span>
						</Tooltip>
					)}
				</Stack>
				<Typography variant="smallText" component="p" sx={{ color: "text.secondary", textAlign: "center", mt: 1 }}>
					Answers are generated from live read-only queries{config ? ` by ${config.model}` : ""}. Check the query details before acting on a number.
				</Typography>
			</Box>
		</Box>
	);
}
